import React, { FC } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { ReduxState } from 'app/redux/types';

import { Icon, IconSize } from 'app/design-system/components/Icon';
import { Hint } from 'app/design-system/components/Popper';
import { ICON_NAME_HELP } from 'app/design-system/constants/IconNames';
import { useTranslation } from 'app/lang';

import { AmountInputModifier, AmountValidationResult, SubmissionState } from '../../../constants';
import type { RecordOfStakeTypeAmountType } from '../../../records';
import { selectHasEnoughMoney } from '../../../selectors';
import { selectAmountStakeType } from '../../../selectors/stakeTypes';
import {
  selectSystemAmountModifier,
  selectSystemMaxWinning,
  selectSystemRank,
  selectSystemRankEntry,
  selectSystemRankOf,
  selectSystemResultingAmount,
  selectValidateSystemAmount,
} from '../../../selectors/stakeTypes/system';
import { selectSubmission } from '../../../selectors/submission';
import { getPresetsAndFreebetsBar } from '../../FooterBar';
import MinLimitError from '../../MinLimitError';
import NoMoneyLink from '../../NoMoneyLink';
import Submission from '../../Submission';
import TextWithAmountBar from '../../TextWithAmountBar';
import SumOfIndividualExpress from '../../Toggle/SumOfIndividualExpress';
import AmountInput from './AmountInput';
import RankSelect from './RankSelect';

import dictionary from './System.dictionary.json';

import styles from './System.module.scss';

// ---

type StoreProps = {
  rank: number | null;
  rankOf: number | null;
  amount: RecordOfStakeTypeAmountType;
  modifier: AmountInputModifier;
  disabled: boolean;
  resultingAmount: number;
  maxWinning: number;
  combinations: number;
  minValue: number;
  minLimitError: boolean;
  hasEnoughMoney: boolean;
};

const System: FC<StoreProps> = ({
  rank,
  rankOf,
  amount,
  modifier,
  disabled,
  resultingAmount,
  maxWinning,
  combinations,
  minValue,
  minLimitError,
  hasEnoughMoney,
}) => {
  const { t } = useTranslation();
  const _textBarClassName = classNames({
    [styles.disabledTextBar]: disabled,
  });

  const hintContent = t(dictionary.hintContent, { combinations });
  const isSumOfIndividualExpressActive = modifier === AmountInputModifier.MULTIPLY;

  const renderFooterContent = () => {
    if (!hasEnoughMoney) {
      return (
        <NoMoneyLink />
      );
    }

    if (minLimitError) {
      return (
        <MinLimitError value={minValue} />
      );
    }

    if (amount.value) {
      return (
        <>
          {isSumOfIndividualExpressActive && (
            <TextWithAmountBar value={amount.numeric}
                               data-test="stakeTypeSystem-amountForEachExpress"
                               label={dictionary.amountForEachExpress}
                               className={_textBarClassName} />
          )}
          <TextWithAmountBar value={resultingAmount}
                             label={isSumOfIndividualExpressActive
                               ? dictionary.writtenOff
                               : dictionary.wholeSystem}
                             data-test="stakeTypeSystem-resultingAmount"
                             className={_textBarClassName} />
          <TextWithAmountBar value={maxWinning}
                             data-test="stakeTypeSystem-maxWinning"
                             label={dictionary.maxWinning}
                             className={_textBarClassName} />
        </>
      );
    }
    const PresetsAndFreebetsBar = getPresetsAndFreebetsBar('system');
    return (
      <PresetsAndFreebetsBar />
    );
  };

  return (
    <div className={styles.container}
         data-test="stakeTypeSystem-container">
      {disabled ? (
        <Submission className={styles.submission} />
      ) : (
        <>
          <div className={styles.rankHeader}
               data-test="stakeTypeSystem-rankHeader">
            <span data-test="stakeTypeSystem-rank">
              {t(dictionary.rankText, { rank: rank || 0 })}
            </span>
            <span data-test="stakeTypeSystem-combinations">
              {t(dictionary.combinationsText, { combinations: combinations || 0 })}
            </span>
            {rank !== null && rankOf !== null && (
              <RankSelect value={rank}
                          of={rankOf}
                          className={styles.rankSelect} />
            )}
          </div>

          <AmountInput hasError={!disabled && (!hasEnoughMoney || minLimitError)} />

          {renderFooterContent()}

          <div className={styles.toggleContainer}
               data-test="stakeTypeSystem-toggleContainer">
            <SumOfIndividualExpress />
            <Hint content={hintContent}
                  data-test="stakeTypeSystem-hint"
                  placement="left">
              <Icon icon={ICON_NAME_HELP}
                    size={IconSize.SMALL}
                    className={styles.tooltipIcon} />
            </Hint>
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const rankEntry = selectSystemRankEntry(state);
  const validation = selectValidateSystemAmount(state);

  return ({
    rank: selectSystemRank(state),
    rankOf: selectSystemRankOf(state),
    amount: selectAmountStakeType(state),
    modifier: selectSystemAmountModifier(state),
    disabled: selectSubmission(state) !== SubmissionState.AWAITING,
    resultingAmount: selectSystemResultingAmount(state) || 0,
    maxWinning: selectSystemMaxWinning(state),
    combinations: rankEntry?.combinations || 0,
    minValue: rankEntry?.min || 0,
    minLimitError: validation === AmountValidationResult.MIN_NOT_REACHED,
    hasEnoughMoney: selectHasEnoughMoney(state),
  });
};

export default connect(mapStateToProps)(System);
